<template>
  <div id="add-fb-form">
    <a-form>
      <a-form-item label="Page" v-if="fbConnected" :label-col="{ span: 5 }" :wrapperCol="{ span: 13 }">
        <a-select
          show-search
          option-filter-prop="children"
          :filter-option="selectionFilterOption"
          placeholder="Select a Page"
          size="large"
          @change="pageSelected"
        >
          <a-select-option v-for="page in fbPages" :key="page.id">{{ page.name }}</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="Form" v-if="currFbPage" :label-col="{ span: 5 }" :wrapperCol="{ span: 13 }">
        <a-select
          :placeholder="fbFormsLoading ? 'Loading...' : 'Select a Form'"
          size="large"
          v-if="currFbPage"
          @change="formSelected"
        >
          <a-select-option v-for="form in sortedForms" :key="form.id" class="select-option">
            {{ form.name }}
            <span class="form-id">{{ form.id }}</span>
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item v-if="currFbForm">
        <field-mappings
          :checking-form="checkingForm"
          :is-production="isProduction"
          :working-fields="workingFields"
          :show-endpoint-switch="true"
          :send-to-regular-endpoint="sendToRegularEndpoint"
        ></field-mappings>
      </a-form-item>
      <a-form-item class="center-buttons" :wrapper-col="{ offset: 5, span: 13 }" v-if="fbPagesLoaded">
        <a-button
          :disabled="canSave"
          :loading="savingForm"
          class="create-button"
          type="primary"
          v-if="currFbForm"
          @click="saveForm()"
          >{{ savingForm ? "Saving" : "Create Form Mapping" }}</a-button
        >
        <a-button @click="connectToFB()" :type="fbConnected ? 'default' : 'primary'" v-if="!working">
          {{ fbConnected ? "Refresh Pages from Facebook" : "Connect to Facebook" }}
        </a-button>
      </a-form-item>
    </a-form>
    <a-spin size="large" v-if="!fbPagesLoaded || working"></a-spin>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import fbSdk from "@/facebook";
import FieldMappings from "@/components/FieldMappings";

export default {
  components: {
    FieldMappings,
  },
  computed: {
    ...mapState([
      "apiUrl",
      "fbForms",
      "fbFormsLoading",
      "fbMappedKeys",
      "fbPages",
      "fbPagesLoaded",
      "isErrorJsonData",
      "jsonData",
      "isProduction",
      "sendToRegularEndpoint",
      "user",
      "workingFields",
      "currFbForm",
      "currFbPage",
    ]),
    canSave() {
      return this.isErrorJsonData;
    },
    fbConnected() {
      if (this.fbPagesLoaded && this.fbPages.length > 0) {
        return true;
      }
      return false;
    },
    sortedForms() {
      return [...this.fbForms].sort((a, b) => {
        if (a.created_time > b.created_time) {
          return -1;
        }
        if (a.created_time < b.created_time) {
          return 1;
        }
        return 0;
      });
    },
  },
  data() {
    return {
      fbInited: false,
      checkingForm: false,
      savingForm: false,
      // indicates that work is going on and updates the UI accordingly
      working: false,
    };
  },
  methods: {
    connectToFB() {
      this.fbInited = false;

      FB.login(
        async (response) => {
          if (response.status !== "connected") {
            alert("You must accept connecting to the application in order to allow subscribing to forms.");
            this.working = false;
            return;
          }
          // make sure a long-term token is generated for the user
          // on the server so a non-expiring page token can be granted
          // let it just get called then run in the background without
          // checking for anything because it won't be needed until the
          // user has selected a page and a form to work with.
          //
          // this call is asynchronous, no need to wait for it. Worst case scenario
          // it fails and they'll have to try again with FB.
          await axios.request({
            method: "post",
            url: this.apiUrl + "/setup-lt-token",
            headers: {
              Authorization: this.user.signInUserSession.idToken.jwtToken,
            },
            params: {
              user_token: response.authResponse.accessToken,
              user_id: response.authResponse.userID,
            },
          });
          // go ahead and load up the pages that the user has authorized to use.
          console.log("going to load up pages authorized to use");
          const fbPages = await this.loadPages();

          // this has the side effect of saving the pages to the server for
          // retrieving non-expiring tokens for the pages as well as saving
          // them for later use.
          this.$store.dispatch("saveFbPages", {
            pages: fbPages,
            setPages: true,
          });
          this.working = false;
        },
        {
          scope:
            "ads_management,leads_retrieval,pages_manage_ads,pages_manage_metadata,pages_read_engagement,pages_read_user_content",
        }
      );
      this.working = true;
    },
    // deals with doing a callback rather than promise for unknown number of times to load pages
    async loadPages() {
      const fbPages = [];
      // used to indicate if we need to get another page of pages
      let next = "";
      // FB.api doesn't support Promises so we need to create this nested function to convert the call from a callback strategy
      // to a promise strategy for pagination purposes
      const loadSet = async (nextKey = "") => {
        return new Promise((resolve, reject) => {
          const params = {};
          if (nextKey !== "") {
            params["after"] = nextKey;
          }
          FB.api("/me/accounts", params, (response) => {
            if (response.err) {
              alert("Problem getting pages from Facebook.");
              resolve({ err: response.err });
            } else if (response) {
              resolve(response);
            }
          });
        });
      };

      do {
        const rslt = await loadSet(next);
        if (rslt.err) {
          // problem loading the data, just jump out here and let whatever we've loaded be returned
          next = "";
        } else {
          fbPages.push(...rslt.data);
          if (rslt.paging.next) {
            // we've got another page of results, go ahead and set things up for it
            next = rslt.paging.cursors.after;
          } else {
            // nothing left
            next = "";
          }
        }
      } while (next != "");
      return fbPages;
    },
    formSelected(value) {
      this.$store.dispatch(
        "setWorkingForm",
        this.fbForms.find((form) => form.id == value)
      );
    },
    pageSelected(value) {
      this.$store.dispatch(
        "setWorkingPage",
        this.fbPages.find((page) => page.id == value)
      );
    },
    submitMappings() {
      this.checkingForm = true;
      this.saveForm();
    },
    saveForm() {
      if (this.isErrorJsonData || JSON.stringify(this.jsonData) == "") {
        alert("Invalid JSON value");
        return;
      }
      this.savingForm = true;
      const hideLoading = this.$message.loading("Saving");
      axios({
        method: "post",
        url: this.apiUrl + "/mappings",
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "UPDATE_MAPPING",
          pageId: this.currFbPage.id,
          formId: this.currFbForm.id,
        },
        data: {
          pageName: this.currFbPage.name,
          formName: this.currFbForm.name,
          jsonData: this.jsonData,
          fbMappedKeys: this.fbMappedKeys,
          isProduction: this.isProduction,
          sendToRegularEndpoint: this.sendToRegularEndpoint
        },
      }).then(
        (rslt) => {
          hideLoading();
          if (rslt.data.status == "ok") {
            alert("Mapping created.");
            this.$router.push("/");
          } else {
            alert("There was a problem saving the mapping: " + rslt.data.message);
          }
          this.savingForm = false;
        },
        (err) => {
          console.log(err);
          hideLoading();
          alert("Unable to save the mapping. Server responded with: " + err);
          this.savingForm = false;
        }
      );
    },
    selectionFilterOption(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
  },
  mounted() {
    this.$store.dispatch("loadSavedFbPages");
    fbSdk().then((rslt) => {
      this.fbInited = true;
    });
  },
};
</script>

<style lang="scss" scoped>
#add-fb-form {
  display: flex;
  flex-direction: column;
}
.create-button {
  margin-right: 10px;
}
.form-id {
  color: #aaa;
  margin-left: 5px;
}
::v-deep {
  .center-buttons .ant-form-item-control {
    text-align: center;
  }
}
</style>
